import { type ReactQuery } from 'cms-api'

import Image from 'next/image'
import Link from 'next/link'
import { rem } from 'polished'
import React, { useRef } from 'react'

import styled from 'styled-components'
import { Navigation, Pagination, EffectFade, Autoplay, A11y } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import {
  BULLET_WRAPPER_SIZE_PX,
  CarouselNavigation,
} from 'components/CarouselNavigation'
import { Box, Container, Flex, Grid } from 'components/Layout'
import { Text } from 'components/Typography'
import { SCHEMA_TYPES } from 'constants/common'
import { COLOR_NAMES, FONT_STACK, SPACE, Z_INDEX } from 'Theme'

const BULLET_THRESHOLD = 10
const BULLET_ACTIVE_CLASS = 'expert-testimonials-pagination-bullet-active'
const AUTOPLAY_DELAY = 100000

const StyledSwiper = styled(Swiper)`
  user-select: none;
  height: 100%;
  .swiper-slide {
    height: auto;
  }
  .swiper-pagination-bullet {
    transform: scale(1) !important;
    transition: all 600ms cubic-bezier(0.4, 0, 0.2, 1);
  }
  .${BULLET_ACTIVE_CLASS} {
    transform: scale(1.5) !important;
    background-color: ${COLOR_NAMES.PINEY_LAKE};
    opacity: 1;
  }

  .swiper-pagination-bullets {
    min-width: ${rem(
      BULLET_WRAPPER_SIZE_PX * BULLET_THRESHOLD
    )}; // bullet wrapper size*max number of bullets visible at once
    column-gap: 20px;
    padding: 16px;
    position: relative;
  }
`

const Testimonial = ({
  name,
  testimonial,
  attachment,
  url,
}: ReactQuery.WidgetsExpertTestimonialsItemComponent) => (
  <Grid
    gridTemplateColumns={{ MOBILE: '1fr', TABLET: 'repeat(2, 1fr)' }}
    gap={rem(32)}
    height={{ MOBILE: '100%', TABLET: 'auto' }}
  >
    <Box>
      <Image
        alt={name}
        src={attachment.url}
        width="0"
        height="0"
        sizes="100vw"
        style={{ width: '100%', aspectRatio: '1/1' }}
        priority
        quality={90}
        loading="eager"
      />
    </Box>
    <Flex
      backgroundColor={COLOR_NAMES.WARP_DRIVE}
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      px={{ MOBILE: rem(20), TABLET: rem(40), DESKTOP: rem(80) }}
      py={{ MOBILE: rem(40), TABLET: 0 }}
      height={{ MOBILE: '100%', TABLET: 'auto' }}
    >
      <Text
        color={COLOR_NAMES.EEIRE_BLACK}
        fontSize={{ MOBILE: rem(24), DESKTOP: rem(32) }}
        fontFamily={FONT_STACK.SERIF}
        textAlign="center"
      >
        What{' '}
        <Link href={url}>
          <Text
            color={COLOR_NAMES.PINEY_LAKE}
            fontSize={{ MOBILE: rem(24), DESKTOP: rem(32) }}
            fontFamily={FONT_STACK.SERIF}
          >
            {name}
          </Text>
        </Link>{' '}
        <br />
        is saying:
      </Text>
      <Box width={rem(73)} height={rem(1)} bg="#949494" my={rem(26)} />
      <Text textAlign="center" fontSize={{ MOBILE: rem(14), DESKTOP: rem(16) }}>
        “{testimonial}”
      </Text>
    </Flex>
  </Grid>
)

export const StrapiExpertTestimonialsWidget: React.FC<
  React.PWC<ReactQuery.WidgetsExpertTestimonialsComponent>
> = ({ testimonials }) => {
  const nextButtonRef = useRef(null)
  const prevButtonRef = useRef(null)
  const paginationRef = useRef(null)
  const swiper = useRef(null)

  return (
    <Box
      pb={{ MOBILE: SPACE.PX_48, TABLET: rem(20) }}
      pt={{ MOBILE: SPACE.PX_64, TABLET: rem(66) }}
    >
      <Container>
        <StyledSwiper
          ref={swiper}
          modules={[Autoplay, Navigation, Pagination, EffectFade, A11y]}
          navigation={{
            prevEl: prevButtonRef.current,
            nextEl: nextButtonRef.current,
          }}
          onBeforeInit={(swiperInstance) => {
            // Assign refs manually before init
            if (typeof swiperInstance.params.navigation !== 'boolean') {
              // eslint-disable-next-line no-param-reassign
              swiperInstance.params.navigation!.prevEl = prevButtonRef.current
              // eslint-disable-next-line no-param-reassign
              swiperInstance.params.navigation!.nextEl = nextButtonRef.current
            }
            if (typeof swiperInstance.params.pagination !== 'boolean') {
              // eslint-disable-next-line no-param-reassign
              swiperInstance.params.pagination!.el = paginationRef.current
            }
          }}
          speed={750}
          pagination={{
            el: paginationRef.current,
            clickable: true,
            bulletActiveClass: BULLET_ACTIVE_CLASS,
            dynamicBullets: testimonials.length > BULLET_THRESHOLD,
          }}
          slidesPerView={1}
          direction="horizontal"
          loop
          autoplay={{ delay: AUTOPLAY_DELAY, disableOnInteraction: false }}
          allowTouchMove
          itemScope
          itemType={SCHEMA_TYPES.ITEM_LIST}
          spaceBetween={20}
        >
          {testimonials.map((testimonial) => (
            <SwiperSlide key={testimonial.id}>
              <Testimonial key={testimonial.id} {...testimonial} />
            </SwiperSlide>
          ))}
          <Flex
            position="relative"
            width="100%"
            px={SPACE.PX_16}
            justifyContent="center"
            zIndex={Z_INDEX.CAROUSEL_NAVIGATION_WRAPPER}
            mt={rem(20)}
          >
            <CarouselNavigation
              prevButtonRef={prevButtonRef}
              nextButtonRef={nextButtonRef}
              paginationRef={paginationRef}
              hasArrows={false}
            />
          </Flex>
        </StyledSwiper>
      </Container>
    </Box>
  )
}
